import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Alert,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import css from 'styled-jsx/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import BrandingContext from '../../Context/BrandingContext';
import IconsContext from '../../Context/IconsContext';
import CenterContainer from '../CenterContainer';
import { ERROR_LOGIN_FORM } from '../../Constants/TrackerClasses';
import TermsAndConditions from './TermsAndConditionsTemplate';

const { className: inputClass, styles: inputStyles } = css.resolve`
  input {
    border-radius: 15px;
  }
  input::placeholder {
    color: var(--icon-link-gray);
  }
  .form-control:focus {
    outline: none;
  }
  input::-webkit-credentials-auto-fill-button {
    visibility: hidden;
    display: none !important;
    pointer-events: none;
    position: absolute;
    right: 0;
  }
  input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px white inset;
  }
`;

const { className: alertClass, styles: alertStyles } = css.resolve`
  .alert {
    background: var(--brand-primary-alert);
    opacity: 0.7;
    right: 12.5%;
    top: 5rem;
    z-index: 10000;
  }
  .alert-hidden {
    opacity: 0;
    transition: all 250ms linear;
  }
`;

const { className: modalClass, styles: modalStyles } = css.resolve`
  @media (max-width: 767px) {
    .modal-dialog {
      height: 100%;
      width: 100%;
      max-height: 100%;
      max-width: 100%;
      margin: 0;
    }
    .modal-body {
      max-height: calc(100vh - 123px);
      overflow-y: auto;
    }
  }
  @media (min-width: 768px) {
    .modal-body {
      max-height: 60vh;
      overflow-y: auto;
    }
  }
`;

const AlertDisplay = ({ showAlert, toggleAlert, alertMessage }) => {
  return (
    <>
      <Alert
        toggle={toggleAlert}
        className={`${alertClass} position-fixed w-75 border-0 ${
          showAlert ? null : 'alert-hidden'
        }`}
      >
        <div
          className={`d-flex align-items-center justify-content-center text-white alert-font-size`}
        >
          {alertMessage}
        </div>
      </Alert>
      {alertStyles}
      <style jsx>{`
        .alert-font-size {
          font-size: 1rem;
        }
      `}</style>
    </>
  );
};
AlertDisplay.propTypes = {
  showAlert: PropTypes.bool,
  toggleAlert: PropTypes.func,
  fadeOut: PropTypes.string,
  alertMessage: PropTypes.string,
};

const HeaderDisplay = () => {
  const {
    isElevatUi,
    isSupraUi,
    isVersatileUi,
    logo_url,
    tige_login_logo_url,
  } = useContext(BrandingContext);
  const { certificateIcon } = useContext(IconsContext);

  const isTigeDemoUi = window.location.host?.includes('tige');
  const hideConnectingText = isVersatileUi || isSupraUi || isTigeDemoUi;

  return (
    <>
      <div className={`text-center mt-${hideConnectingText ? '4' : '2'}`}>
        <Row className={isElevatUi ? '' : isSupraUi ? 'mb-5' : 'mb-3'}>
          <img
            alt="brand"
            className="login-logo image-fluid mw-100 mx-auto"
            src={isTigeDemoUi ? tige_login_logo_url : logo_url}
          />
        </Row>
        <p
          className={
            hideConnectingText
              ? 'd-none'
              : 'text-center text-elevat-blue three-quarter-text mx-auto mb-4'
          }
        >
          Connecting the Industrial World
        </p>
        <div className="two-factor-container text-center text-text-gray mb-4">
          <span>
            <img
              alt="two-factor-icon"
              className="login-two-factor-icon"
              src={certificateIcon}
            />
          </span>
          Two Factor Authentication
        </div>
      </div>
      <style jsx>{`
        .two-factor-container {
          margin-top: 40px;
          font-size: 0.8rem;
          white-space: nowrap;
        }
        .login-two-factor-icon {
          max-height: 20px;
          margin-right: 5px;
          margin-bottom: 5px;
        }
        .login-logo {
          max-height: 70px;
        }
        .elevat-text {
          font-size: 1.2rem;
          margin-left: 2px;
        }
        @media (min-width: 768px) {
          .login-logo {
            max-height: 100px;
          }
        }
      `}</style>
    </>
  );
};

const FormDisplay = ({
  handleTwoFactor,
  challenge,
  handlePropUpdate,
  showError,
  twoFactorError,
  handleSubmit,
  loginRequestPending,
}) => {
  const { isSupraUi, isVersatileUi } = useContext(BrandingContext);

  return (
    <>
      <Form onSubmit={handleTwoFactor}>
        <FormGroup row style={{ marginTop: '40px', marginBottom: '0px' }}>
          <Label
            style={{ marginBottom: '5px' }}
            className="text-center text-text-light-gray"
            for="login-two-factor"
          >
            Authentication Code
          </Label>
          <div className="input-group">
            <Input
              className={`bg-white shadow-none ${inputClass} border-${
                showError
                  ? 'danger'
                  : isVersatileUi
                    ? 'input-border-gray'
                    : isSupraUi
                      ? 'dark'
                      : 'elevat-blue'
              }`}
              type="text"
              id="challenge"
              name="challenge"
              placeholder="123456"
              value={challenge}
              onChange={handlePropUpdate}
              invalid={showError}
              autoComplete="one-time-code"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus // Not a variable. A boolean attribute of <Input />. True unless explicitly set to False.
            />
          </div>
          <p
            style={{ marginTop: '10px' }}
            className="text-text-light-gray three-quarter-text"
          >
            We&apos;ll remember you for 24 hours
          </p>
        </FormGroup>
        <div className="error-container row align-items-center">
          {showError && (
            <span
              style={{ marginLeft: '0px', paddingBottom: '20px' }}
              className={`text-danger error-text ${ERROR_LOGIN_FORM}`}
            >
              {twoFactorError}
            </span>
          )}
        </div>
        <div className="resend-code-container row text-text-light-gray text-decoration-none shadow-none">
          <span>
            We just sent a verification code to your phone number. Didn&apos;t
            get it?{' '}
            <button
              className="btn btn-link mx-auto resend-code-link text-decoration-none shadow-none p-0"
              type="button"
              onClick={handleSubmit}
            >
              Resend the code.
            </button>
          </span>
        </div>
        <Row>
          <button
            className={`btn mt-auto login-button${
              isVersatileUi ? '-versatile' : ''
            } flex-grow-1 form-border-radius d-flex align-items-center justify-content-center`}
            type="submit"
            disabled={loginRequestPending}
          >
            <span
              className={`login-button-text${
                isVersatileUi ? '-versatile' : ''
              }`}
            >
              {loginRequestPending ? (
                <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
              ) : (
                'Verify'
              )}
            </span>
          </button>
        </Row>
      </Form>
      {inputStyles}
      <style jsx>{`
        .login-button {
          height: 35px;
          background-color: var(--login-button-background-color);
          cursor: pointer;
        }
        .login-button:focus,
        .login-button:hover {
          background-color: var(--login-button-hover-background-color);
          outline: none;
        }
        .login-button-versatile {
          background-color: var(--white);
          border-color: var(--brand-primary);
          color: var(--brand-primary);
          cursor: pointer;
          height: 35px;
        }
        .login-button-versatile:focus,
        .login-button-versatile:hover {
          background-color: var(--brand-primary);
          color: var(--white);
        }
        .login-button-text {
          color: var(--white);
          font-size: 1.2rem;
        }
        .login-button-text-versatile {
          color: inherit;
          font-size: 1.2rem;
        }
        .login-button-text-versatile:focus,
        .login-button-text-versatile:hover {
          color: var(--white);
        }
        .resend-code-container {
          font-size: 0.8rem !important;
          color: var(--black-text);
          margin-bottom: 30px;
        }
        .resend-code-link {
          color: var(--brand-primary);
          cursor: pointer;
          font-size: 0.8rem !important;
        }
        .resend-code-link:hover {
          color: var(
            --${isVersatileUi || isSupraUi ? 'brand-primary' : 'highlight-blue'}
          ) !important;
        }
        .form-border-radius {
          border-radius: 15px;
        }
        .error-container {
          height: 20px;
        }
        .error-text {
          margin-left: 10px;
          font-size: 0.81rem;
        }
      `}</style>
    </>
  );
};
FormDisplay.propTypes = {
  handleSubmit: PropTypes.func,
  handleTwoFactor: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  challenge: PropTypes.string,
  showError: PropTypes.bool,
  twoFactorError: PropTypes.string,
  loginRequestPending: PropTypes.bool,
};

const FooterDisplay = ({ toggleTermsAndConditionsModal, termsModal }) => {
  const { contact_url, isSupraUi, isVersatileUi } = useContext(BrandingContext);

  return (
    <>
      <div className="login-form-footer d-flex flex-column justify-content-center align-items-center flex-md-row justify-content-md-between align-items-md-end">
        <div className="text-nowrap pl-lg-4">
          <span>
            <span className="branded-text">powered by </span>
            <span className="branded-text font-weight-light">elevāt</span>
          </span>
        </div>
        <div className="text-nowrap">
          <span className="branded-text">Don&apos;t have an account?</span>
          <a
            className="login-form-link ml-2"
            href={contact_url}
            rel="noopener noreferrer"
            target="_blank"
          >
            Contact Us
          </a>
        </div>
        <div className="text-nowrap pr-lg-4">
          <button
            onClick={toggleTermsAndConditionsModal}
            className="btn m-0 p-0 login-form-link"
          >
            Terms and Conditions
          </button>
        </div>
        <Modal
          className={modalClass}
          isOpen={termsModal}
          toggle={toggleTermsAndConditionsModal}
          style={{
            maxWidth: '620px',
          }}
        >
          <ModalHeader
            className="border-0"
            toggle={toggleTermsAndConditionsModal}
          >
            Terms of Service
          </ModalHeader>
          <ModalBody className={`${modalClass} text-left`}>
            <TermsAndConditions />
          </ModalBody>
          <ModalFooter>
            <button
              className="btn btn-outline-secondary py-0 px-3 mr-3"
              onClick={toggleTermsAndConditionsModal}
            >
              Close
            </button>
          </ModalFooter>
        </Modal>
      </div>
      {modalStyles}
      <style jsx>{`
        .login-form-footer {
          font-size: 0.8rem;
          line-height: 1.2rem;
        }
        .login-form-link {
          font-size: 0.8rem !important;
          text-decoration: none;
          color: var(--${isVersatileUi ? 'elevat-blue' : 'black-text'});
          cursor: pointer;
          box-shadow: none;
        }
        .login-form-link:focus,
        .login-form-link:hover {
          text-decoration: none;
          color: var(
            --${isSupraUi ? 'brand-primary' : 'highlight-blue'}
          ) !important;
        }
        .login-form-link:active,
        .login-form-link:visited,
        .login-form-link:focus {
          text-decoration: none;
        }
        .branded-text {
          color: var(--${isVersatileUi ? 'text-white' : 'elevat-blue'});
        }
        @media (max-width: 768px) {
          .login-form-footer {
            max-height: 50px;
          }
        }
        @media (min-width: 768px) {
          .login-form-footer {
            margin-top: 60px;
          }
        }
      `}</style>
    </>
  );
};
FooterDisplay.propTypes = {
  toggleTermsAndConditionsModal: PropTypes.func,
  termsModal: PropTypes.bool,
};

export default function MFADisplay(props) {
  const {
    alertMessage,
    challenge,
    handlePropUpdate,
    handleSubmit,
    handleTwoFactor,
    loginRequestPending,
    showAlert,
    showError,
    termsModal,
    toggleAlert,
    toggleTermsAndConditionsModal,
    twoFactorError,
  } = props;
  const { isVersatileUi } = useContext(BrandingContext);

  return (
    <CenterContainer>
      <Container fluid={true} className="p-0">
        <Row>
          <AlertDisplay
            showAlert={showAlert}
            toggleAlert={toggleAlert}
            alertMessage={alertMessage}
          />
          <div className="col align-self-center">
            <div className="outer-container d-flex flex-column mx-auto shadow p-3 bg-white rounded">
              <Col xs={{ size: 10, offset: 1 }} md={{ size: 6, offset: 3 }}>
                <HeaderDisplay />
                <FormDisplay
                  handleSubmit={handleSubmit}
                  handleTwoFactor={handleTwoFactor}
                  handlePropUpdate={handlePropUpdate}
                  challenge={challenge}
                  twoFactorError={twoFactorError}
                  showError={showError}
                  loginRequestPending={loginRequestPending}
                />
              </Col>
              {!isVersatileUi ? (
                <div className="mt-auto">
                  <FooterDisplay
                    termsModal={termsModal}
                    toggleTermsAndConditionsModal={
                      toggleTermsAndConditionsModal
                    }
                  />
                </div>
              ) : null}
            </div>
            {isVersatileUi ? (
              <div
                className="mx-auto mt-2 text-white"
                style={{ maxWidth: '620px' }}
              >
                <FooterDisplay
                  termsModal={termsModal}
                  toggleTermsAndConditionsModal={toggleTermsAndConditionsModal}
                />
              </div>
            ) : null}
          </div>
        </Row>
      </Container>
      <style jsx>{`
        .outer-container {
          height: ${isVersatileUi ? '500' : '550'}px;
          max-width: 620px;
          color: var(--black-text);
        }
        @media (min-width: 768px) {
          .outer-container {
            height: ${isVersatileUi ? '550' : '600'}px;
          }
        }
      `}</style>
    </CenterContainer>
  );
}
MFADisplay.propTypes = {
  alertMessage: PropTypes.string,
  challenge: PropTypes.string,
  handleSubmit: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  handleTwoFactor: PropTypes.func,
  loginRequestPending: PropTypes.bool,
  showAlert: PropTypes.bool,
  showError: PropTypes.bool,
  termsModal: PropTypes.bool,
  toggleAlert: PropTypes.func,
  toggleTermsAndConditionsModal: PropTypes.func,
  twoFactorError: PropTypes.string,
};
